import React from "react";
import { ArrowRight, Star } from "lucide-react";
import { siteConfig as fallback } from "../mock";
import { Icon } from "../lib/icons";
import { img, contactLink } from "../lib/api";
import { useSite } from "../context/SiteContext";
import WaveDivider from "./WaveDivider";

const renderTitle = (title, highlight) => {
  if (!highlight || !title.includes(highlight)) {
    return <span className="gold-gradient-text">{title}</span>;
  }
  const [before, after] = title.split(highlight);
  return (
    <>
      {before}
      <span className="gold-gradient-text">{highlight}</span>
      {after}
    </>
  );
};

const Hero = ({ onShop }) => {
  const { settings } = useSite();
  const s = settings || fallback;
  const badges = s.trustBadges || fallback.trustBadges;
  return (
    <section className="liquid-bg relative overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(#d4af37 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 md:pt-24 md:pb-28 relative">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal">
            <div className="inline-flex items-center gap-2 bg-navy-700 gold-border rounded-full px-4 py-1.5 mb-6">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={13} className="fill-gold text-gold" />
                ))}
              </div>
              <span className="text-xs text-slate-200">
                {s.rating} &middot; {s.reviewCount} reviews
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-white">
              {renderTitle(s.heroTitle || fallback.heroTitle || "Premium 1:1 Prop Bills", s.heroHighlight)}
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-lg leading-relaxed">
              {s.heroSubtitle || fallback.heroSubtitle}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button onClick={onShop} data-testid="hero-shop-now-btn" className="group btn-liquid text-navy font-bold px-8 py-4 rounded-xl shadow-xl shadow-gold/20">
                <span className="relative z-10 inline-flex items-center gap-2">{s.heroCtaText || "Shop Now"}
                <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" /></span>
              </button>
              <a {...contactLink(s.contact?.telegramLink)} data-testid="hero-talk-btn" className="inline-flex items-center gap-2 text-white glass-navy-card hover:border-gold px-7 py-3.5 rounded-xl transition-colors duration-200">
                Talk to Us
              </a>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {badges.map((b) => (
                <div key={b.label} className="flex items-center gap-2 text-slate-200">
                  <span className="h-9 w-9 rounded-lg glass-navy-card flex items-center justify-center text-gold">
                    <Icon name={b.icon} size={16} />
                  </span>
                  <span className="text-xs font-medium">{b.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="reveal relative">
            <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-tr from-gold/30 via-sky-500/20 to-indigo-500/30 blur-2xl liquid-bg" />
            <div className="relative rounded-3xl overflow-hidden gold-border shadow-2xl shadow-black/50 img-zoom">
              <img src={img(s.heroImage || "/products/100s-1.jpeg")} alt="Premium prop bills" className="w-full h-[420px] md:h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
            </div>
            <div className="floaty absolute -bottom-6 -left-6 glass-gold-card rounded-2xl px-5 py-4 shadow-xl hidden sm:block">
              <p className="text-gold font-display font-bold text-2xl">1:1</p>
              <p className="text-slate-300 text-xs">Camera-ready quality</p>
            </div>
            <div className="floaty absolute -top-5 -right-4 btn-liquid rounded-2xl px-5 py-4 shadow-xl hidden sm:block" style={{ animationDelay: "1.5s" }}>
              <p className="text-navy font-display font-bold text-2xl">Tracked</p>
              <p className="text-navy/80 text-xs font-medium">Fast shipping</p>
            </div>
          </div>
        </div>
      </div>
      <WaveDivider />
    </section>
  );
};

export default Hero;
